/**
 * Daily response-time trend per site — the board's L1 trend chart (dashboard
 * design rules §2: trend over time → `Chart.Line`).
 *
 * Series selection follows the board's one filter row: with sites picked in
 * the filter those are charted; with no selection the slowest sites lead,
 * because they are the ones worth watching. Colours are fixed per site from
 * the categorical config built over the FULL estate (§3.3), so filtering
 * never repaints a surviving line. A day with no recorded checks renders as a
 * gap, never as zero (§5).
 */

import { Chart } from "@norican/dataviz";
import { EmptyState } from "@norican/react";
import { useMemo, useState } from "react";

import { ChartCard } from "@/components/board";
import type { MonitoringHistory, SiteUptimeHistory } from "@/lib/api";
import { identityConfig } from "@/lib/charts/config";

import { formatDay, formatMs } from "./status";

/** How many sites to chart when the filter has no selection. */
const DEFAULT_SERIES = 5;
/** Hard cap so a broad selection stays a readable chart, not spaghetti. */
const MAX_SERIES = 8;

/** One chart row: the day plus one latency value (or gap) per charted site. */
type LatencyRow = { day: string } & Record<string, string | number | null>;

function shortDay(iso: string): string {
  const at = new Date(`${iso}T00:00:00Z`);
  // An unparseable date renders as the literal "Invalid Date" otherwise, which
  // then sits on an axis or a caption looking like a value. `src/features/
  // security/status.ts` already guards this way; this matches it.
  if (Number.isNaN(at.getTime())) {
    return "—";
  }
  return at.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

/** Mean of a site's recorded daily response times, `null` when unmeasured. */
function meanLatency(site: SiteUptimeHistory): number | null {
  const values = site.days
    .map((day) => (day.checks ? day.avg_response_time_ms : null))
    .filter((value): value is number => value != null);
  if (values.length === 0) {
    return null;
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

interface LatencyTrendProps {
  /** The full (unfiltered) history payload — fixes the colour assignment. */
  history: MonitoringHistory;
  /** Site ids selected in the board's filter row (empty = no selection). */
  selectedIds: string[];
}

export function LatencyTrend({ history, selectedIds }: LatencyTrendProps) {
  const [hidden, setHidden] = useState<string[]>([]);
  const toggle = (key: string) =>
    setHidden((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );

  // Colour assignment over the FULL estate, alphabetical — stable under filters.
  const config = useMemo(
    () => identityConfig(history.sites.map((site) => site.name).sort((a, b) => a.localeCompare(b))),
    [history.sites],
  );

  const shown = useMemo(() => {
    const measured = history.sites.filter((site) => meanLatency(site) != null);
    const picked =
      selectedIds.length > 0
        ? measured.filter((site) => selectedIds.includes(site.id))
        : [...measured]
            .sort((a, b) => (meanLatency(b) ?? 0) - (meanLatency(a) ?? 0))
            .slice(0, DEFAULT_SERIES);
    return picked.slice(0, MAX_SERIES);
  }, [history.sites, selectedIds]);

  const rows = useMemo<LatencyRow[]>(() => {
    const days = [...new Set(shown.flatMap((site) => site.days.map((day) => day.day)))].sort();
    return days.map((day) => {
      const row: LatencyRow = { day };
      for (const site of shown) {
        const rollup = site.days.find((candidate) => candidate.day === day);
        // No checks recorded that day → a gap in the line, never a zero.
        row[site.name] = rollup?.checks ? (rollup.avg_response_time_ms ?? null) : null;
      }
      return row;
    });
  }, [shown]);

  const hasData = rows.some((row) => shown.some((site) => row[site.name] != null));

  const caption =
    selectedIds.length > 0
      ? shown.length < selectedIds.length
        ? `First ${shown.length} of ${selectedIds.length} selected sites`
        : "Selected sites"
      : `Slowest ${shown.length} sites — pick others in the filter above`;

  return (
    <ChartCard
      title="Response time trend"
      caption={`${caption} · daily mean of recorded checks`}
      tableTwin={{
        id: "latency-trend",
        ariaLabel: "Daily average response time per site",
        data: rows,
        getRowId: (row) => row.day,
        columns: [
          {
            id: "day",
            header: "Day",
            accessor: (row) => row.day,
            sortable: true,
            cell: (row) => <span className="tabular-nums">{formatDay(row.day)}</span>,
          },
          ...shown.map((site) => ({
            id: site.id,
            header: site.name,
            align: "end" as const,
            numeric: true,
            accessor: (row: LatencyRow) => (row[site.name] as number | null) ?? null,
            cell: (row: LatencyRow) => (
              <span className="tabular-nums">{formatMs(row[site.name] as number | null)}</span>
            ),
          })),
        ],
      }}
    >
      {!hasData ? (
        <EmptyState
          size="sm"
          title="No response times recorded yet"
          description="Daily latency rollups accrue from the first poll cycle onward."
        />
      ) : (
        <Chart.Container
          config={config}
          aria-label="Daily average response time per site"
          style={{ height: 300 }}
        >
          <Chart.LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
            <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
            <Chart.XAxis {...Chart.axisProps} dataKey="day" tickFormatter={shortDay} />
            <Chart.YAxis
              {...Chart.axisProps}
              width={56}
              tickFormatter={(value: number) => formatMs(value)}
            />
            <Chart.Tooltip
              content={
                <Chart.TooltipContent
                  formatter={(value) => formatMs(Number(value))}
                  labelFormatter={(label) => formatDay(String(label))}
                />
              }
            />
            {shown.map((site) => (
              <Chart.Line
                key={site.id}
                {...Chart.seriesDefaults}
                dataKey={site.name}
                type="monotone"
                stroke={config[site.name]?.color}
                strokeWidth={1.5}
                dot={false}
                hide={hidden.includes(site.name)}
              />
            ))}
            {shown.length > 1 && (
              <Chart.Legend content={<Chart.LegendContent hidden={hidden} onToggle={toggle} />} />
            )}
          </Chart.LineChart>
        </Chart.Container>
      )}
    </ChartCard>
  );
}
